import { createAction } from 'redux-actions'
import {
  creditCardDialogOpened,
  creditCardDialogClosed,
  creditCardAdded,
  creditCardErrored
} from 'analytics'
import Stripe from 'lib/stripe'
import * as errors from 'lib/utils/errors'
import { error, success } from '@segment/notify'
import request from 'superagent'
import fetch from 'lib/fetch'

/**
 * Action types.
 */

export const ADDING_CREDIT_CARD = 'ADDING_CREDIT_CARD'
export const ADD_CREDIT_CARD = 'ADD_CREDIT_CARD'
export const ADD_CREDIT_CARD_ERROR = 'ADD_CREDIT_CARD_ERROR'
export const UPDATING_BILLING_PLAN = 'UPDATING_BILLING_PLAN'
export const UPDATE_BILLING_PLAN_ERROR = 'UPDATE_BILLING_PLAN_ERROR'
export const UPDATED_BILLING_PLAN = 'UPDATED_BILLING_PLAN'
export const REQUEST_BILLING_BY_WORKSPACE_SLUG = 'REQUEST_BILLING_BY_WORKSPACE_SLUG'
export const REQUEST_BILLING_BY_WORKSPACE_SLUG_ERROR = 'REQUEST_BILLING_BY_WORKSPACE_SLUG_ERROR'
export const RECEIVE_BILLING_BY_WORKSPACE_SLUG = 'RECEIVE_BILLING_BY_WORKSPACE_SLUG'
export const REQUEST_PAY_INVOICE = 'REQUEST_PAY_INVOICE'
export const REQUEST_PAY_INVOICE_ERROR = 'REQUEST_PAY_INVOICE_ERROR'
export const RECEIVE_PAY_INVOICE = 'RECEIVE_PAY_INVOICE'

/**
 * Actions.
 */

const addingCreditCard = createAction(ADDING_CREDIT_CARD)
const addCreditCard = createAction(ADD_CREDIT_CARD)
const addCreditCardError = createAction(ADD_CREDIT_CARD_ERROR)
const updatingBillingPlan = createAction(UPDATING_BILLING_PLAN)
const updateBillingPlanError = createAction(UPDATE_BILLING_PLAN_ERROR)
const updatedBillingPlan = createAction(UPDATED_BILLING_PLAN)
const requestBillingByWorkspaceSlug = createAction(REQUEST_BILLING_BY_WORKSPACE_SLUG)
const requestBillingByWorkspaceSlugError = createAction(REQUEST_BILLING_BY_WORKSPACE_SLUG_ERROR)
const receiveBillingByWorkspaceSlug = createAction(RECEIVE_BILLING_BY_WORKSPACE_SLUG)

export const updateBillingPlan = ({ workspaceSlug, newPlan }) => dispatch => {
  return new Promise((resolve, reject) => {
    dispatch(updatingBillingPlan({ workspaceSlug }))

    fetch(`/api/workspaces/${workspaceSlug}/billing/plan`, {
      method: 'PUT',
      body: JSON.stringify({ id: newPlan.id })
    }).then(res => {
      if (!res.ok) {
        if (res.status === 401) {
          throw new Error('Sorry, you’re not authorized to make that change.')
        } else {
          throw new Error('There was a problem updating your plan. Please try again.')
        }
      }

      if (res.ok) {
        success('Your plan was successfully updated.')
        dispatch(updatedBillingPlan({ workspaceSlug, plan: newPlan }))
        return resolve(res)
      }
    }).catch(err => {
      error(err.message)
      dispatch(updateBillingPlanError({ workspaceSlug, error: err }))
      reject(err)
    })
  })
}

export const addCreditCardByWorkspaceSlug = (slug, billingEmail, plan, isUpgradingPlans) => (dispatch, getState) => {
  return new Promise((resolve, reject) => {
    const { billingByWorkspaceSlug } = getState()
    const billing = billingByWorkspaceSlug[slug].data

    dispatch(addingCreditCard({ slug }))

    const stripe = new Stripe({
      email: billingEmail,
      workspaceSlug: slug
    })

    stripe.on('open', function () {
      creditCardDialogOpened(billing)
    })

    stripe.on('cancelled', function () {
      creditCardDialogClosed()
      reject()
    })

    stripe.addCard(function (err, res) {
      if (err) {
        errors.track(err, {
          tags: { section: 'billing', type: 'add-card-failure' },
          extra: { workspaceSlug: slug }
        })
        error('There was a problem adding this card. Please try again.')
        dispatch(addCreditCardError({ slug, err }))
        creditCardErrored(billing, 'There was a problem adding this card. Please try again.')
        reject(err)
        return
      }

      if (isUpgradingPlans) {
        dispatch(updateBillingPlan({ workspaceSlug: slug, newPlan: plan }))
        success('Congratulations!  Your plan has been updated.')
      } else {
        success('Thanks! Your card has been added to your billing')
      }
      dispatch(addCreditCard({ slug, card: res.body.card }))

      // HACK: update the ref to `billing` since we pass it around
      //       (this prevents exceptions in our analytics)
      billing.card = res.body.card

      creditCardAdded(billing)
      resolve(res)
      return
    })
  })
}

export const payLatestInvoice = (slug) => {
  const onSuccess = () => success('Thanks! Your payment has been made.')
  const onFail = (err) => {
    errors.track(err, {
      tags: { section: 'billing', type: 'pay-latest-invoice-failure' },
      extra: { workspaceSlug: slug }
    })
    error('There was a problem making your payment. Please try again.')
  }

  return {
    types: {
      request: REQUEST_PAY_INVOICE,
      receive: RECEIVE_PAY_INVOICE,
      failure: REQUEST_PAY_INVOICE_ERROR
    },
    callAPI: () => fetch(`/api/workspaces/${slug}/billing/charges/latest/pay`, {
      method: 'POST'
    }).then(
      res => res.ok ? onSuccess() : onFail() && res.json(),
      err => onFail(err)
    ),
    payload: { slug }
  }
}

export const addCreditCardAndUpdateBillingPlan = params => dispatch => {
  const { workspaceSlug, email, newPlan } = params

  return dispatch(addCreditCardByWorkspaceSlug(workspaceSlug, email))
    .then((res) => !res.ok ? res : dispatch(updateBillingPlan({ workspaceSlug, newPlan })))
}

const fetchBillingByWorkspaceSlug = workspaceSlug => dispatch => {
  dispatch(requestBillingByWorkspaceSlug({ workspaceSlug }))
  const url = `/api/workspaces/${workspaceSlug}/billing`
  return loadBillingWorkspaceByURL(url, workspaceSlug, dispatch)
}

export const fetchBillingByWorkspaceSlugWithUsageData = workspaceSlug => dispatch => {
  dispatch(requestBillingByWorkspaceSlug({ workspaceSlug }))
  const url = `/api/workspaces/${workspaceSlug}/billing/with-usage`
  return loadBillingWorkspaceByURL(url, workspaceSlug, dispatch)
}

export const fetchBillingByWorkspaceSlugIfNeeded = workspaceSlug => (dispatch, getState) => {
  const state = getState()
  if (shouldFetchBillingByWorkspaceSlug(workspaceSlug, state)) {
    return dispatch(fetchBillingByWorkspaceSlug(workspaceSlug))
  }

  return Promise.resolve()
}

function loadBillingWorkspaceByURL (url, workspaceSlug, dispatch) {
  return new Promise((resolve, reject) => {
    request
    .get(url)
    .end(function (err, res) {
      if (err || !res.ok) {
        error('There was an error fetching your billing infomation')
        dispatch(requestBillingByWorkspaceSlugError({ workspaceSlug }))
        reject(err)
      } else {
        // HACK: dependencies on `window.segment` across the app require this
        // object to be populated, like legacy billing routes
        if (window.segment && window.segment.workspace) {
          window.segment.workspace.billing = res.body
        }
        dispatch(receiveBillingByWorkspaceSlug({ workspaceSlug, data: res.body }))
        resolve(res.body)
      }
    })
  })
}

function shouldFetchBillingByWorkspaceSlug (workspaceSlug, state) {
  const billing = state.billingByWorkspaceSlug[workspaceSlug]
  if (!billing) return true
  if (billing.isRequesting) return false
  return billing.didInvalidate
}



// WEBPACK FOOTER //
// ./actions/billing.js